import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiRequest } from '../utils/api';
import { Link, useNavigate } from 'react-router-dom';
import { useAlert } from '../components/AlertModal';
import { pdf } from '@react-pdf/renderer';
import SalesSummaryModal from '../components/SalesSummaryModal';
import SellNotePdfDocument from '../components/SellNotePdfDocument';
import SellNotePdfThreeColumn from '../components/SellNotePdfThreeColumn';
import { useAuthContext } from '../context/AuthContext';
import '../styles/TransactionHistoryList.css';

const TRANSACTION_TYPE_LABELS = {
  deposit: '入金',
  withdrawal: '出金',
  expenses: '経費',
  purchase_payment: '買取',
  refund: '返金',
};

const TRANSACTION_TYPE_OPTIONS = Object.entries(TRANSACTION_TYPE_LABELS);

const TransactionHistoryList = () => {
  const [salesReceipts, setSalesReceipts] = useState([]);
  const [cashTransactions, setCashTransactions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  });
  const [receiptId, setReceiptId] = useState('');
  const [editingCashId, setEditingCashId] = useState(null);
  const [editedCash, setEditedCash] = useState(null);
  const [selectedRegisterId, setSelectedRegisterId] = useState(null);
  const [noteSummary, setNoteSummary] = useState(null);
  const [showSalesModal, setShowSalesModal] = useState(false); // 売上表示モーダル用

  const [registers, setRegisters] = useState([]);
  const [salesPage, setSalesPage] = useState(1);
  const [cashPage, setCashPage] = useState(1);
  const [salesTotalPages, setSalesTotalPages] = useState(1);
  const [cashTotalPages, setCashTotalPages] = useState(1);
  const pageSize = 20;

  const requestControllerRef = useRef(null);
  const noteControllerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(new Date()); 
  const dayOfWeek = currentTime.toLocaleString('ja-JP', { weekday: 'long' }); // 曜日
  const formattedDate = currentTime.toLocaleDateString('ja-JP'); // 日付
  const formattedTime = currentTime.toLocaleTimeString('ja-JP'); // 時間
  const [closings, setClosings] = useState([]);
  const [selectedClosingId, setSelectedClosingId] = useState("");

  // ジャーナル印刷中
  const [isPrintingJournal, setIsPrintingJournal] = useState(false);

  // 清算前のIDを記録
  const closingIdsBeforeModalRef = useRef(null);

  // 印刷確認の重複防止
  const promptedClosingIdsRef = useRef(new Set());

  useEffect(() => {
    return () => {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      if (noteControllerRef.current) {
        noteControllerRef.current.abort();
      }
    };
  }, []);

  const { showAlert } = useAlert();
  const { user: userInfo } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (userInfo) {
      setSelectedRegisterId(userInfo.is_superuser ? null : userInfo.register_id);
    }
  }, [userInfo]);

  useEffect(() => {
    const fetchRegisters = async () => {
      try {
        const res = await apiRequest("inventory/registers/");
        setRegisters(res);
      } catch (e) {
        console.error("レジ一覧取得エラー:", e);
      }
    };

    fetchRegisters();
  }, []);

  // 清算一覧を最新データで取得
  const fetchClosings = useCallback(async ({ selectLatest = false } = {}) => {

    if (!selectedDate || !selectedRegisterId) {
      setClosings([]);
      setSelectedClosingId("");
      return [];
    }

    const query = new URLSearchParams({
      date: selectedDate,
      register_id: String(selectedRegisterId),
    });

    const res = await apiRequest(
      `inventory/register_closing_list_for_print/?${query.toString()}`,
      "GET"
    );

    const rows = Array.isArray(res)
      ? res
      : (res?.results ?? []);

    // 新しい清算を先頭にする
    const sorted = [...rows].sort(
      (a, b) => Number(b.id) - Number(a.id)
    );

    setClosings(sorted);

    // 最新の清算を自動選択
    setSelectedClosingId((prev) => {

      if (!sorted.length) return "";

      if (
        selectLatest ||
        !sorted.some(c => String(c.id) === String(prev))
      ) {
        return String(sorted[0].id);
      }

      return prev;
    });

    return sorted;

  }, [selectedDate, selectedRegisterId]);


  // 日付・レジ変更時に再取得
  useEffect(() => {

    fetchClosings().catch((error) => {
      console.error("清算一覧取得エラー:", error);
    });

  }, [fetchClosings]);

  const fetchData = async (date, registerId = selectedRegisterId, sPage = 1, cPage = 1) => {
    if (!registerId) return;

    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }

    const controller = new AbortController();
    requestControllerRef.current = controller;

    const params = new URLSearchParams({
      date,
      register_id: registerId,
      page_size: pageSize,
    });

    try {
      const receipts = await apiRequest(
        `inventory/sales_receipts/?${params.toString()}&page=${sPage}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );

      const cash = await apiRequest(
        `inventory/cash_transactions/?${params.toString()}&page=${cPage}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );

      setSalesReceipts(receipts.results ?? receipts);
      setCashTransactions(cash.results ?? cash);

      setSalesPage(sPage);
      setCashPage(cPage);
      setSalesTotalPages(Math.max(1, Math.ceil((receipts.count ?? receipts.length ?? 0) / pageSize)));
      setCashTotalPages(Math.max(1, Math.ceil((cash.count ?? cash.length ?? 0) / pageSize)));
    } catch (e) {
      if (e.name === "AbortError") return;
      console.error("データ取得エラー:", e);
    }
  };

  useEffect(() => {
    if (selectedRegisterId) {
      fetchData(selectedDate, selectedRegisterId, 1, 1);
    }
  }, [selectedDate, selectedRegisterId]);

  const fetchNoteSummary = useCallback(async () => {
    if (noteControllerRef.current) {
      noteControllerRef.current.abort();
    }

    const controller = new AbortController();
    noteControllerRef.current = controller;

    try {
      const res = await apiRequest(
        `inventory/note_sales_details_by_date/?date=${selectedDate}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );
      setNoteSummary(res);
    } catch (e) {
      if (e.name === "AbortError") return;
      console.error("販売ノート取得失敗:", e);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchNoteSummary();
  }, [selectedDate, fetchNoteSummary]);

  const handleSearch = async () => {
    if (receiptId) {
      try {
        const receipt = await apiRequest(`inventory/sales_receipts/${receiptId}/`);
        setSalesReceipts([receipt]);
      } catch (e) {
        showAlert("レシートが見つかりません");
        setSalesReceipts([]);
      }
    } else {
      fetchData(selectedDate);
    }
  };

  const handleEditCash = (tx) => {
    setEditingCashId(tx.id);
    setEditedCash({ ...tx });
  };

  const handleSaveCash = async () => {
    try {
      const updated = await apiRequest(`inventory/cash_transactions/${editingCashId}/`, 'PATCH', {
        transaction_type: editedCash.transaction_type,
        amount: editedCash.amount,
      });

      const updatedList = cashTransactions.map((tx) =>
        tx.id === updated.id ? { ...updated } : tx
      );
      setCashTransactions(updatedList);
      setEditingCashId(null);
      setEditedCash(null);
    } catch {
      showAlert("保存に失敗しました");
    }
  };

  const handleSettlementPreview = async () => {
    try {
      const registerId = selectedRegisterId || 2;

      const data = await apiRequest(
        `inventory/register_closing/print/?date=${selectedDate}&register_id=${registerId}`,
        "GET"
      );

      await window.electronAPI.settlementPreview(data);
    } catch (error) {
      console.error("清算レシートプレビューエラー:", error);
      showAlert("清算レシートプレビューに失敗しました");
    }
  };
  
// ジャーナル印刷
  const handleJournalPrintByClosing = async (
    closingId = selectedClosingId
  ) => {

    if (!closingId) {
      showAlert("清算を選択してください。");
      return;
    }

    if (!window.electronAPI?.journalPrint) {
      showAlert("Electronのジャーナル印刷機能が利用できません。");
      return;
    }

    try {

      setIsPrintingJournal(true);

      const data = await apiRequest(
        `inventory/journal/print/?closing_id=${encodeURIComponent(closingId)}`,
        "GET"
      );

      const result = await window.electronAPI.journalPrint(data);

      if (result?.success === false) {
        throw new Error(
          result?.error || "印刷処理に失敗しました"
        );
      }

    } catch (error) {

      console.error("ジャーナル印刷エラー:", error);
      showAlert("ジャーナル印刷に失敗しました。");

    } finally {

      setIsPrintingJournal(false);

    }
  };


  // 清算完了後に印刷するか確認
  const askToPrintJournal = async (closingId) => {

    if (!closingId) return;

    const id = String(closingId);

    // 同じ清算で確認を繰り返さない
    if (promptedClosingIdsRef.current.has(id)) return;

    promptedClosingIdsRef.current.add(id);

    // 今回の清算を選択
    setSelectedClosingId(id);

    const print = window.confirm(
      "清算が完了しました。\n今回のジャーナルを印刷しますか？"
    );

    if (print) {
      await handleJournalPrintByClosing(id);
    }

  };


  // 清算保存完了時の処理
  const handleClosingComplete = async (closingData) => {

    try {

      // 最新の清算一覧を取得
      const latest = await fetchClosings({
        selectLatest: true
      });

      const returnedId =
        typeof closingData === "number" ||
        typeof closingData === "string"
          ? closingData
          : (closingData?.id ?? closingData?.closing_id);

      // 今回作成された清算を取得
      const created = returnedId
        ? latest.find(
            c => String(c.id) === String(returnedId)
          )
        : (
            closingIdsBeforeModalRef.current
              ? latest.find(
                  c => !closingIdsBeforeModalRef.current.has(
                    String(c.id)
                  )
                )
              : null
          );

      if (created) {

        // 印刷確認を表示
        await askToPrintJournal(created.id);

      } else {

        showAlert(
          "清算は保存されましたが、新しい清算が一覧に見つかりません。"
        );

      }

    } catch (error) {

      console.error("清算後の一覧更新エラー:", error);

      showAlert(
        "清算後の一覧更新に失敗しました。"
      );

    }

  };


  // 売上表示モーダルを開く
  const openSalesModal = async () => {

    try {

      // 清算前のデータを記録
      const before = await fetchClosings();

      closingIdsBeforeModalRef.current = new Set(
        before.map(c => String(c.id))
      );

    } catch (error) {

      console.error("清算前の一覧取得エラー:", error);

      closingIdsBeforeModalRef.current = null;

    }

    setShowSalesModal(true);

  };


  // 売上表示モーダルを閉じる
  const closeSalesModal = async () => {

    setShowSalesModal(false);

    try {

      const beforeIds = closingIdsBeforeModalRef.current;

      // 最新データを取得
      const latest = await fetchClosings({
        selectLatest: true
      });

      if (beforeIds) {

        // モーダルを開いた後に作成された清算を探す
        const created = latest.find(
          c => !beforeIds.has(String(c.id))
        );

        if (created) {

          // ジャーナル印刷確認
          await askToPrintJournal(created.id);

        }

      }

    } catch (error) {

      console.error(
        "清算モーダル終了後の一覧更新エラー:",
        error
      );

      showAlert("清算一覧の更新に失敗しました。");

    } finally {

      closingIdsBeforeModalRef.current = null;

    }

  };

  
  const handleSellnotePrint = async (date, registerId, summaryData) => {
    if (!window.electronAPI?.sendSellNotePdf) {
      alert("Electronの印刷機能が利用できません");
      return;
    }
  
    try {
      console.log("🔧 印刷処理開始:", { date, registerId });
  
      // 1. set_items に含まれるIDを全収集
      const allSetIds = [];
      Object.values(summaryData).forEach((register) =>
        ['normal', 'tax_exempt'].forEach((type) =>
          Object.values(register?.[type] || {}).forEach((payment) =>
            Object.values(payment).flat().forEach((item) => {
              if (item.set_items) {
                const ids = item.set_items
                  .split(',')
                  .map((s) => parseInt(s.trim()))
                  .filter((id) => !isNaN(id));
                item._set_items_ids = ids;
                allSetIds.push(...ids);
              }
            })
          )
        )
      );
  
      console.log("🧾 収集したset_items ID:", allSetIds);
  
      const uniqueIds = [...new Set(allSetIds)];
      console.log("📦 ユニークID一覧:", uniqueIds);
  
      // 2. bulk_get APIで一括取得
      const bulkItems = await apiRequest(`inventory/bulk_get/?ids=${uniqueIds.join(',')}`);
      console.log("📄 bulk_get取得結果:", bulkItems);
  
      const idMap = {};
      bulkItems.forEach((item) => {
        idMap[item.id] = item;
      });
  
      // 3. 各 item に set_items_details を付与
      Object.values(summaryData).forEach((register) =>
        ['normal', 'tax_exempt'].forEach((type) =>
          Object.values(register?.[type] || {}).forEach((payment) =>
            Object.values(payment).flat().forEach((item) => {
              if (item._set_items_ids) {
                item.set_items_details = item._set_items_ids
                  .map((id) => idMap[id])
                  .filter(Boolean);
              }
            })
          )
        )
      );
  
      console.log("✅ set_items_details 結合完了");
  
      // 4. PDF生成
      const component =
        Number(registerId) === 2
          ? <SellNotePdfDocument date={date} registerId={registerId} summaryData={summaryData} />
          : <SellNotePdfThreeColumn date={date} registerId={registerId} summaryData={summaryData} />;
  
      const blob = await pdf(component).toBlob();
      console.log("📄 PDF生成完了");
  
      const arrayBuffer = await blob.arrayBuffer();
      console.log("📨 Electronへ送信中...");
  
      window.electronAPI.sendSellNotePdf(arrayBuffer);
      console.log("🚀 印刷完了");
  
    } catch (err) {
      console.error("❌ 印刷失敗:", err);
      alert("印刷に失敗しました");
    }
  };



  return (
    <>
    <div className="TransactionHistoryList-pageHeader">
      <button
        className="TransactionHistoryList-backButton"
        onClick={() => navigate(-1)}
      >
        ◀ 戻る
      </button>

      <div>
        <h1 className="TransactionHistoryList-title">取引履歴・清算管理</h1>
        <div className="TransactionHistoryList-subtitle">
          {selectedDate} / レジID: {selectedRegisterId || "-"}
        </div>
      </div>
    </div>

    <div className="TransactionHistoryList-topBar">

      {/* 左：検索 */}
      <div className="TransactionHistoryList-searchInline">
        <label>
          日付
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </label>

        <label>
          レシート
          <input
            type="number"
            value={receiptId}
            onChange={(e) => setReceiptId(e.target.value)}
            placeholder="番号"
          />
        </label>

        <button onClick={handleSearch} className="primary">
          検索
        </button>
      </div>

      {/* 右：清算・印刷 */}
      <div className="TransactionHistoryList-actionsInline">
        <label>
          <select
            value={selectedClosingId}
            onChange={(e) => setSelectedClosingId(e.target.value)}
          >
            <option value="">清算を選択</option>
            {closings.map((closing) => (
              <option key={closing.id} value={closing.id}>
                {closing.label}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={() => handleJournalPrintByClosing()}
          disabled={!selectedClosingId || isPrintingJournal}
        >
          {isPrintingJournal ? "印刷中..." : "ジャーナル"}
        </button>

        <button
          onClick={() => handleSellnotePrint(selectedDate, selectedRegisterId, noteSummary)}
        >
          ノート
        </button>

        <button onClick={handleSettlementPreview}>
          清算レシート
        </button>

        <button onClick={openSalesModal}>
          売上表示
        </button>
      </div>

    </div>
    <div className="TransactionHistoryList-registerTabs">
      {registers.map((reg) => (
        <button
          key={reg.id}
          type="button"
          className={
            Number(selectedRegisterId) === Number(reg.id)
              ? "TransactionHistoryList-registerTab active"
              : "TransactionHistoryList-registerTab"
          }
          onClick={() => {
            setSelectedRegisterId(reg.id);
            setSalesPage(1);
            setCashPage(1);
          }}
        >
          {reg.name}
        </button>
      ))}
    </div>

    <div className="TransactionHistoryList-container">
      <div className="TransactionHistoryList-column TransactionHistoryList-sales">
        <div className="TransactionHistoryList-sectionHeader">
          <h2>販売履歴</h2>
          <span>{salesReceipts.length}件表示</span>
        </div>

        <ul className="TransactionHistoryList-cardList">
          {salesReceipts.map((receipt) => (
            <li key={receipt.id} className="TransactionHistoryList-historyCard">
              <Link to={`/transaction_history/${receipt.id}`} className="TransactionHistoryList-historyLink">
                <div className="TransactionHistoryList-historyMain">
                  <div>
                    <div className="TransactionHistoryList-receiptNo">
                      レシート #{receipt.id}
                    </div>
                    <div className="TransactionHistoryList-historyDate">
                      {new Date(receipt.date).toLocaleString()}
                    </div>
                  </div>

                  <div className="TransactionHistoryList-historyAmount">
                    ¥{(
                      Number(receipt.total_amount || 0) +
                      Number(receipt.tax_amount || 0)
                    ).toLocaleString()}
                  </div>
                </div>

                <div className="TransactionHistoryList-historyMeta">
                  <span className="TransactionHistoryList-badge">
                    {receipt.payment_method || "販売"}
                  </span>

                  {receipt.sale_type === "tax_exempt" && (
                    <span className="TransactionHistoryList-badge taxFree">
                      {receipt.tax_free_mode === "refund"
                        ? "免税・REFUND"
                        : "免税"}
                    </span>
                  )}

                  {receipt.is_edited && (
                    <span className="TransactionHistoryList-edited">
                      修正済
                    </span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
        <div className="TransactionHistoryList-pagination">
            <button
              disabled={salesPage <= 1}
              onClick={() => fetchData(selectedDate, selectedRegisterId, salesPage - 1, cashPage)}
            >
              ← 前
            </button>
            <span>{salesPage} / {salesTotalPages}</span>
            <button
              disabled={salesPage >= salesTotalPages}
              onClick={() => fetchData(selectedDate, selectedRegisterId, salesPage + 1, cashPage)}
            >
              次 →
            </button>
          </div>
      </div>
      <div className="TransactionHistoryList-column TransactionHistoryList-others">
        <div className="TransactionHistoryList-sectionHeader">
          <h2>その他取引</h2>
          <span>{cashTransactions.length}件表示</span>
        </div>

        <ul className="TransactionHistoryList-cardList">
          {cashTransactions.map((tx) => (
            <li key={tx.id} className="TransactionHistoryList-historyCard">
              {editingCashId === tx.id ? (
                <div className="TransactionHistoryList-editRow">
                  <select
                    value={editedCash.transaction_type}
                    onChange={(e) =>
                      setEditedCash({ ...editedCash, transaction_type: e.target.value })
                    }
                  >
                    {TRANSACTION_TYPE_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>

                  <input
                    type="number"
                    value={editedCash.amount}
                    onChange={(e) =>
                      setEditedCash({ ...editedCash, amount: e.target.value })
                    }
                  />

                  <button onClick={handleSaveCash}>保存</button>
                  <button onClick={() => setEditingCashId(null)}>キャンセル</button>
                </div>
              ) : (
                <>
                  <div className="TransactionHistoryList-historyMain">
                    <div>
                      <div className="TransactionHistoryList-receiptNo">
                        {TRANSACTION_TYPE_LABELS[tx.transaction_type] || tx.transaction_type}
                      </div>
                      <div className="TransactionHistoryList-historyDate">
                        {new Date(tx.date).toLocaleString()}
                      </div>
                    </div>

                    <div className="TransactionHistoryList-historyAmount cash">
                      ¥{Number(tx.amount).toLocaleString()}
                    </div>
                  </div>

                  <div className="TransactionHistoryList-historyMeta">
                    <span className={`TransactionHistoryList-badge type-${tx.transaction_type}`}>
                      {TRANSACTION_TYPE_LABELS[tx.transaction_type] || tx.transaction_type}
                    </span>

                    {tx.is_edited && (
                      <span className="TransactionHistoryList-edited">
                        修正済
                      </span>
                    )}

                    <button
                      onClick={() => handleEditCash(tx)}
                      className="TransactionHistoryList-smallButton"
                    >
                      編集
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        <div className="TransactionHistoryList-pagination">
            <button
              disabled={cashPage <= 1}
              onClick={() => fetchData(selectedDate, selectedRegisterId, salesPage, cashPage - 1)}
            >
              ← 前
            </button>
            <span>{cashPage} / {cashTotalPages}</span>
            <button
              disabled={cashPage >= cashTotalPages}
              onClick={() => fetchData(selectedDate, selectedRegisterId, salesPage, cashPage + 1)}
            >
              次 →
            </button>
          </div>
      </div>
    </div>
    {showSalesModal && (
      <SalesSummaryModal
        closeModal={closeSalesModal}
        onClosingComplete={handleClosingComplete}
        dataNow={formattedDate}
        dayOfWeek={dayOfWeek}
        timeNow={formattedTime}
        register={selectedRegisterId}
      />
    )}
    </>
  );
};

export default TransactionHistoryList;
